/**
 * The raid page's live connection.
 *
 * A raid lobby is the one place in this app where being a few seconds stale is
 * a real cost: a trainer taps a lobby showing 3/5 that filled up ten seconds
 * ago, and the join fails after they have already committed to it. So the list
 * and the lobby are pushed, not polled.
 *
 * The socket carries NO mutations. Joining, readying and starting all go over
 * the authenticated REST endpoints, and their effects arrive back down here as
 * broadcast state. That keeps one authority (the Worker decides who you are, the
 * hub decides the state) and means a dropped socket degrades to "the screen
 * stops updating", never to "my join was lost".
 *
 * Reconnect is deliberately simple: backoff to a ceiling, resubscribe to
 * whatever the caller last asked to watch. A raid lasts minutes, so a connection
 * that cannot recover inside a minute has failed the user regardless of how
 * cleverly it keeps trying.
 */
import { getAuthToken } from "@/lib/queryClient";
import { API_BASE } from "@/lib/apiBase";
import { INDEX_SHARD } from "@/lib/raidBossKey";
import type { RaidBossArtRef } from "@/lib/formSpriteCandidates";

export type RaidBossTier = "1" | "3" | "5" | "mega" | "max";

export interface RaidBossRef {
  speciesId: number | null;
  name: string;
  formId: string | null;
  tier: RaidBossTier;
  canBeShiny: boolean;
  /** `boosted` is null for a Max Battle — that encounter has no weather boost. */
  hundoCp: { normal: number; boosted: number | null } | null;
}

/**
 * Adapt a hub boss to the art-resolution shape shared with the LeekDuck
 * roster (raidBossArtCandidates / raidBossSilhouetteSlug in
 * formSpriteCandidates.ts), or null for an unresolvable species. `isMega` is
 * the "mega" tier; Max Battle bosses take the regular path — the Dynamax dex
 * has no form art or atlas tiles, so base-species art is correct for them.
 */
export function raidBossArtRef(
  boss: Pick<RaidBossRef, "speciesId" | "name" | "formId" | "tier">,
): RaidBossArtRef | null {
  if (boss.speciesId == null) return null;
  return {
    speciesId: boss.speciesId,
    isMega: boss.tier === "mega",
    name: boss.name,
    formId: boss.formId,
  };
}

export interface LobbySummary {
  id: string;
  hostAccountId: number;
  hostTrainerName: string | null;
  boss: RaidBossRef;
  occupancy: number;
  capacity: number;
  minTrainerLevel: number | null;
  requiresRaidPass: boolean;
  createdAt: number;
  expiresAt: number;
}

export type MemberState = "adding" | "ready" | "left";

export interface LobbyMember {
  accountId: number;
  trainerName: string | null;
  joinOrder: number;
  joinedAt: number;
  state: MemberState;
  readyAt: number | null;
  leftAt: number | null;
}

export interface LobbyState {
  id: string;
  hostAccountId: number;
  hostTrainerName: string | null;
  boss: RaidBossRef;
  maxJoiners: number;
  minTrainerLevel: number | null;
  requiresRaidPass: boolean;
  status: "open" | "started" | "cancelled";
  createdAt: number;
  startedAt: number | null;
  expiresAt: number;
  members: LobbyMember[];
}

export type ClientMessage =
  | { type: "watch-list"; patches?: boolean }
  | { type: "watch-lobby"; id: string }
  | { type: "watch-bosses"; patches?: boolean }
  | { type: "resync-list" }
  | { type: "resync-bosses" }
  | { type: "backgrounding" };

/** One raid boss, as the browse screen draws it. Counts, never lobbies — see
 *  cloudflare/src/durable/RaidIndex.ts for why that distinction is the whole
 *  point of the index. */
export interface BossRow {
  /** The boss's shard key: also what you open a socket to for its lobbies. */
  id: string;
  boss: RaidBossRef;
  gathering: number;
  nearlyFull: number;
}

export type ServerMessage =
  | { type: "list"; seq: number; lobbies: LobbySummary[] }
  | { type: "list-patch"; seq: number; upsert: LobbySummary[]; remove: string[] }
  | { type: "bosses"; seq: number; rows: BossRow[] }
  | { type: "bosses-patch"; seq: number; upsert: BossRow[]; remove: string[] }
  | { type: "lobby"; lobby: LobbyState }
  | { type: "lobby-gone"; id: string; reason: "started" | "cancelled" | "expired" }
  | { type: "error"; message: string };

/**
 * Most actionable first: bosses with a raid one trainer short of going, then
 * the busiest, then alphabetical so the order is total and a patch stream
 * cannot shuffle equal rows.
 */
function byActivity(a: BossRow, b: BossRow): number {
  if (a.nearlyFull !== b.nearlyFull) return b.nearlyFull - a.nearlyFull;
  if (a.gathering !== b.gathering) return b.gathering - a.gathering;
  return a.boss.name.localeCompare(b.boss.name);
}

/**
 * Newest first, the same rule the hub sorts a snapshot by.
 *
 * The client has to own the ordering now: a patch says WHAT changed and not
 * where it belongs, so the order has to be derivable from data the client
 * already holds. `createdAt` is. If the hub ever ranks the list by something
 * the client cannot recompute — by how close a lobby is to filling, which
 * docs/raid-scaling.md argues for — then the patch has to start carrying the
 * order explicitly, and this comparator is the thing that has to go.
 */
function byNewest(a: LobbySummary, b: LobbySummary): number {
  return b.createdAt - a.createdAt;
}

type Watch = { kind: "list" } | { kind: "lobby"; id: string } | { kind: "bosses" };

export interface RaidHubHandlers {
  /**
   * Which object this connection talks to. `index` is the browse screen; any
   * other key is one boss's lobbies, including the lobby pages for that boss.
   *
   * It is fixed for the life of the connection ON PURPOSE. A socket belongs to
   * exactly one Durable Object, so "switch shard" is not a subscription change,
   * it is a different connection — and a watchList() that silently pointed at
   * the wrong object would show an empty list with nothing to explain it. Open
   * a new RaidHubConnection instead.
   */
  shard?: string;
  onBosses?: (rows: BossRow[]) => void;
  onList?: (lobbies: LobbySummary[]) => void;
  onLobby?: (lobby: LobbyState) => void;
  onGone?: (id: string, reason: "started" | "cancelled" | "expired") => void;
  /** Connection state, so a screen can say "reconnecting" rather than lie. */
  onStatus?: (status: "connecting" | "open" | "closed") => void;
}

const MAX_BACKOFF_MS = 15_000;

export class RaidHubConnection {
  private socket: WebSocket | null = null;
  private watch: Watch = { kind: "list" };
  private handlers: RaidHubHandlers;
  private backoff = 1000;
  private closedByCaller = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private appStateBound = false;
  /**
   * The live list, by id, plus the version the hub says it is.
   *
   * Held rather than passed straight through because the hub now sends what
   * CHANGED. `listSeq` is the whole safety mechanism: a patch is applied only
   * when it is exactly the next one, and anything else asks for a fresh
   * snapshot rather than guessing. A silently mis-applied patch would show a
   * trainer a lobby that is already full, which is precisely the thing the
   * pushed list exists to prevent.
   */
  private listById = new Map<string, LobbySummary>();
  private listSeq = -1;
  /** The same arrangement for the index's boss rows. */
  private bossById = new Map<string, BossRow>();
  private bossSeq = -1;
  private onHidden: (() => void) | null = null;
  private onPageHide: (() => void) | null = null;
  private removeNativeAppState: (() => void) | null = null;

  constructor(handlers: RaidHubHandlers) {
    this.handlers = handlers;
  }

  connect(): void {
    this.closedByCaller = false;
    this.open();
    this.watchAppState();
  }

  /**
   * Tell the hub before the OS backgrounds us.
   *
   * This is what separates "switched to Pokemon GO to add the host" from "the
   * app was killed". The server holds a seat indefinitely for the first and
   * releases it quickly for the second, and it can only tell them apart because
   * of this message — a closed socket looks identical either way.
   *
   * Sent on the native app-state event, with the DOM events as the web
   * fallback. Best effort throughout: a missed announcement costs the trainer
   * their seat after twenty seconds, which is the old behaviour, not a break.
   */
  private watchAppState(): void {
    if (typeof document === "undefined" || this.appStateBound) return;
    this.appStateBound = true;

    const announce = () => {
      if (this.socket?.readyState !== WebSocket.OPEN) return;
      try {
        this.socket.send(JSON.stringify({ type: "backgrounding" }));
      } catch {
        /* the socket is already going; the grace window covers it. */
      }
    };
    this.onHidden = () => {
      if (document.visibilityState === "hidden") announce();
    };
    document.addEventListener("visibilitychange", this.onHidden);
    window.addEventListener("pagehide", announce);
    this.onPageHide = announce;

    void (async () => {
      try {
        const mod = await import("@capacitor/app" as any);
        const App = (mod as any).App;
        if (!App || typeof App.addListener !== "function") return;
        const handle = await App.addListener(
          "appStateChange",
          (state: { isActive: boolean }) => {
            if (!state?.isActive) announce();
          },
        );
        this.removeNativeAppState = () => void handle?.remove?.();
      } catch {
        /* web build — the DOM events above are the whole story there. */
      }
    })();
  }

  /** Switch what this connection is subscribed to, without reconnecting. Only
   *  ever within the shard this connection was opened against. */
  watchList(): void {
    this.watch = { kind: "list" };
    this.subscribe();
  }

  /** Watch the browse screen. Only meaningful on the index shard. */
  watchBosses(): void {
    this.watch = { kind: "bosses" };
    this.subscribe();
  }

  watchLobby(id: string): void {
    this.watch = { kind: "lobby", id };
    // List frames stop while we are inside a lobby, so whatever we hold is
    // about to go stale by an unknown amount. Dropping the seq means the way
    // back out is a snapshot rather than a patch onto an unknown base.
    this.listSeq = -1;
    this.subscribe();
  }

  close(): void {
    this.closedByCaller = true;
    if (this.onHidden) document.removeEventListener("visibilitychange", this.onHidden);
    if (this.onPageHide) window.removeEventListener("pagehide", this.onPageHide);
    this.removeNativeAppState?.();
    this.onHidden = null;
    this.onPageHide = null;
    this.removeNativeAppState = null;
    this.appStateBound = false;
    if (this.retryTimer != null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    try {
      this.socket?.close();
    } catch {
      /* already gone. */
    }
    this.socket = null;
  }

  private open(): void {
    const token = getAuthToken();
    if (!token) {
      // Nothing to connect with. Report closed rather than retrying forever
      // against an endpoint that will keep refusing us.
      this.handlers.onStatus?.("closed");
      return;
    }
    this.handlers.onStatus?.("connecting");
    const base = API_BASE.replace(/^http/, "ws");
    // The shard names the Durable Object to listen to. Defaults to the index,
    // which is the browse screen.
    const shard = this.handlers.shard ?? INDEX_SHARD;
    const url = `${base}/api/raids/socket?token=${encodeURIComponent(token)}&shard=${encodeURIComponent(shard)}`;
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      this.scheduleRetry();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.backoff = 1000;
      // A new socket knows nothing about what the old one had seen. The hub
      // opens with a snapshot, which resets this — but until it lands, refuse
      // to apply a patch onto a list from a previous connection.
      this.listSeq = -1;
      this.bossSeq = -1;
      this.handlers.onStatus?.("open");
      this.subscribe();
    };
    socket.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(ev.data)) as ServerMessage;
      } catch {
        return;
      }
      if (msg.type === "list") {
        this.listById = new Map(msg.lobbies.map((l) => [l.id, l]));
        this.listSeq = msg.seq ?? -1;
        this.emitList();
      } else if (msg.type === "list-patch") {
        // Only ever the very next one. A gap means we missed a frame, and
        // applying this on top of the wrong base would leave the list quietly
        // wrong instead of visibly stale, so ask for the whole thing.
        if (this.listSeq < 0 || msg.seq !== this.listSeq + 1) {
          this.requestResync();
          return;
        }
        for (const lobby of msg.upsert) this.listById.set(lobby.id, lobby);
        for (const id of msg.remove) this.listById.delete(id);
        this.listSeq = msg.seq;
        this.emitList();
      } else if (msg.type === "bosses") {
        this.bossById = new Map(msg.rows.map((r) => [r.id, r]));
        this.bossSeq = msg.seq ?? -1;
        this.emitBosses();
      } else if (msg.type === "bosses-patch") {
        if (this.bossSeq < 0 || msg.seq !== this.bossSeq + 1) {
          this.bossSeq = -1;
          this.send({ type: "resync-bosses" });
          return;
        }
        for (const row of msg.upsert) this.bossById.set(row.id, row);
        for (const id of msg.remove) this.bossById.delete(id);
        this.bossSeq = msg.seq;
        this.emitBosses();
      } else if (msg.type === "lobby") this.handlers.onLobby?.(msg.lobby);
      else if (msg.type === "lobby-gone") this.handlers.onGone?.(msg.id, msg.reason);
    };
    socket.onclose = () => {
      this.socket = null;
      this.handlers.onStatus?.("closed");
      this.scheduleRetry();
    };
    socket.onerror = () => {
      try {
        socket.close();
      } catch {
        /* onclose will still fire, or already has. */
      }
    };
  }

  /** Hand the caller the boss rows in display order. */
  private emitBosses(): void {
    this.handlers.onBosses?.([...this.bossById.values()].sort(byActivity));
  }

  /** Hand the caller the list in display order. */
  private emitList(): void {
    this.handlers.onList?.([...this.listById.values()].sort(byNewest));
  }

  /** Ask for the whole list again, after a gap we cannot bridge. */
  private requestResync(): void {
    this.listSeq = -1;
    this.send({ type: "resync-list" });
  }

  private send(msg: ClientMessage): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    try {
      this.socket.send(JSON.stringify(msg));
    } catch {
      /* the close handler will reconnect and resubscribe. */
    }
  }

  private subscribe(): void {
    if (this.watch.kind === "bosses") {
      this.send({ type: "watch-bosses", patches: true });
    } else if (this.watch.kind === "list") {
      // `patches: true` is this client declaring it can apply a diff. The hub
      // keeps sending whole snapshots to anyone who does not say so, which is
      // what stops a Worker deploy from freezing the raid list on every build
      // already installed on a phone.
      this.send({ type: "watch-list", patches: true });
    } else {
      this.send({ type: "watch-lobby", id: this.watch.id });
    }
  }

  private scheduleRetry(): void {
    if (this.closedByCaller || this.retryTimer != null) return;
    /**
     * Jittered, because every client that drops does so for the SAME reason.
     *
     * The hub is one Durable Object holding every socket in the app. When it
     * restarts, all of them are cut at the same instant, and an exact 1s, 2s,
     * 4s ladder brings all of them back in lockstep — each wave landing on an
     * object that is still serving the previous one, and each reconnect costing
     * a full live-list frame. Spreading each wave across half its window turns
     * a thundering herd into a ramp. The backoff itself still doubles cleanly;
     * only the wait drawn from it is randomised.
     */
    const delay = this.backoff * (0.5 + Math.random());
    this.backoff = Math.min(MAX_BACKOFF_MS, this.backoff * 2);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!this.closedByCaller) this.open();
    }, delay);
  }
}
